import React from 'react';
import { Box, Container, Typography, Button, Avatar } from '@mui/material';
import { KeyboardArrowDown, Email } from '@mui/icons-material';

// Palette locale — cohérente avec le thème (ink / or / papier)
const INK = '#1a1a1a';
const GOLD = '#c9a96e';
const PAPER = '#faf8f5';

const HeroHeader = ({ onScrollToGallery, onContact, onBio }) => {
  return (
    <Box
      component="header"
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        bgcolor: PAPER,
        color: INK,
        // Atmosphère : halos ambre/ink très diffus + texture papier subtile — aucune image externe
        backgroundImage: `
          radial-gradient(60% 55% at 50% 8%, rgba(201,169,110,0.16) 0%, rgba(201,169,110,0) 60%),
          radial-gradient(50% 40% at 85% 100%, rgba(26,26,26,0.06) 0%, rgba(26,26,26,0) 70%),
          radial-gradient(45% 40% at 12% 88%, rgba(201,169,110,0.10) 0%, rgba(201,169,110,0) 70%)
        `,
        // Grain léger
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: 0.4,
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22%3E%3Cfilter id=%22n%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22 opacity=%220.35%22/%3E%3C/svg%3E")',
          zIndex: 0,
        },
        // Filet or en bas de section
        '&::before': {
          content: '""',
          position: 'absolute',
          left: '50%',
          bottom: 0,
          transform: 'translateX(-50%)',
          width: 'min(1200px, 88%)',
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${GOLD}66, transparent)`,
          zIndex: 1,
        },
        // Animation d'entrée en cascade
        '@keyframes riseIn': {
          from: { opacity: 0, transform: 'translateY(24px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes fadeScale': {
          from: { opacity: 0, transform: 'scale(0.92)' },
          to: { opacity: 1, transform: 'scale(1)' },
        },
        '@keyframes floatCue': {
          '0%, 100%': { transform: 'translate(-50%, 0)' },
          '50%': { transform: 'translate(-50%, 8px)' },
        },
      }}
    >
      <Container
        maxWidth="md"
        sx={{ position: 'relative', zIndex: 2, textAlign: 'center', py: { xs: 10, md: 6 }, px: { xs: 3, sm: 4 } }}
      >
        {/* Portrait — cadre à double filet or */}
        <Box
          sx={{
            display: 'inline-flex',
            p: '6px',
            borderRadius: '50%',
            border: `1px solid ${GOLD}`,
            boxShadow: `0 18px 50px rgba(26,26,26,0.14)`,
            mb: 3.5,
            opacity: 0,
            animation: 'fadeScale 1s cubic-bezier(0.22,1,0.36,1) 0.05s forwards',
          }}
        >
          <Avatar
            src="/noelle_b.jpg"
            alt="Noëlle Boronad — artiste peintre"
            sx={{
              width: { xs: 128, md: 156 },
              height: { xs: 128, md: 156 },
              border: `3px solid ${PAPER}`,
              outline: `1px solid ${GOLD}55`,
            }}
          />
        </Box>

        {/* Eyebrow */}
        <Typography
          component="p"
          sx={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: { xs: '0.6rem', md: '0.75rem' },
            fontWeight: 600,
            letterSpacing: { xs: '0.2em', md: '0.34em' },
            textTransform: 'uppercase',
            color: GOLD,
            mb: 2,
            opacity: 0,
            animation: 'riseIn 0.9s cubic-bezier(0.22,1,0.36,1) 0.2s forwards',
          }}
        >
          Artiste peintre · Œuvres originales
        </Typography>

        {/* Nom */}
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic',
            fontWeight: 300,
            color: INK,
            lineHeight: 1.02,
            fontSize: { xs: '2.7rem', sm: '4.2rem', md: '5.4rem' },
            letterSpacing: '-0.015em',
            px: 1,
            mb: 2.5,
            opacity: 0,
            animation: 'riseIn 1s cubic-bezier(0.22,1,0.36,1) 0.32s forwards',
          }}
        >
          Noëlle Boronad
        </Typography>

        {/* Filet or + losange */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            mb: 3,
            opacity: 0,
            animation: 'riseIn 1s cubic-bezier(0.22,1,0.36,1) 0.42s forwards',
          }}
        >
          <Box sx={{ width: { xs: 44, md: 64 }, height: '1px', bgcolor: `${GOLD}` }} />
          <Box sx={{ width: 6, height: 6, transform: 'rotate(45deg)', bgcolor: GOLD }} />
          <Box sx={{ width: { xs: 44, md: 64 }, height: '1px', bgcolor: `${GOLD}` }} />
        </Box>

        {/* Baseline */}
        <Typography
          sx={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic',
            fontWeight: 400,
            fontSize: { xs: '1.15rem', md: '1.6rem' },
            color: 'rgba(26,26,26,0.72)',
            maxWidth: { xs: 300, md: 560 },
            mx: 'auto',
            lineHeight: 1.5,
            mb: 5,
            opacity: 0,
            animation: 'riseIn 1s cubic-bezier(0.22,1,0.36,1) 0.52s forwards',
          }}
        >
          Chaque œuvre raconte une histoire unique, chaque couleur évoque une émotion.
        </Typography>

        {/* Actions */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap',
            opacity: 0,
            animation: 'riseIn 1s cubic-bezier(0.22,1,0.36,1) 0.64s forwards',
          }}
        >
          <Button
            variant="contained"
            size="large"
            endIcon={<KeyboardArrowDown />}
            onClick={onScrollToGallery}
            sx={{
              bgcolor: INK,
              color: PAPER,
              px: 3.5,
              py: 1.4,
              borderRadius: '2px',
              letterSpacing: '0.08em',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#000', boxShadow: '0 10px 24px rgba(26,26,26,0.22)' },
            }}
          >
            Découvrir la galerie
          </Button>
          {onBio && (
            <Button
              variant="outlined"
              size="large"
              onClick={onBio}
              aria-label="Consulter la biographie de l'artiste"
              sx={{
                color: INK,
                borderColor: 'rgba(26,26,26,0.25)',
                px: 3.5,
                py: 1.4,
                borderRadius: '2px',
                letterSpacing: '0.08em',
                boxShadow: 'none',
                '&:hover': { borderColor: GOLD, bgcolor: 'rgba(201,169,110,0.08)' },
              }}
            >
              Biographie
            </Button>
          )}
          <Button
            variant="text"
            size="large"
            startIcon={<Email />}
            onClick={onContact}
            sx={{
              color: 'rgba(26,26,26,0.7)',
              px: 2.5,
              py: 1.4,
              borderRadius: '2px',
              letterSpacing: '0.08em',
              '&:hover': { color: INK, bgcolor: 'transparent', textDecoration: 'underline', textDecorationColor: GOLD },
            }}
          >
            Contact
          </Button>
        </Box>
      </Container>

      {/* Indicateur de défilement */}
      <Box
        onClick={onScrollToGallery}
        role="button"
        aria-label="Faire défiler vers la galerie"
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: 28,
          zIndex: 2,
          cursor: 'pointer',
          color: 'rgba(26,26,26,0.4)',
          transform: 'translateX(-50%)',
          animation: 'floatCue 2.4s ease-in-out infinite',
          display: { xs: 'none', md: 'block' },
          '&:hover': { color: GOLD },
        }}
      >
        <KeyboardArrowDown sx={{ fontSize: 30 }} />
      </Box>
    </Box>
  );
};

export default HeroHeader;
